import { useEffect } from "react";
import { create } from "zustand";
import { preferences, StorageKeys } from "@/services/storage";
import { report } from "@services/Sentry.ts";

interface DarkModeState {
  isDarkMode: boolean;
  isLoaded: boolean;
  setDarkMode: (isDark: boolean) => void;
  setLoaded: (loaded: boolean) => void;
}

export const useDarkModeStore = create<DarkModeState>((set) => ({
  isDarkMode: false,
  isLoaded: false,
  setDarkMode: (isDark) => set({ isDarkMode: isDark }),
  setLoaded: (loaded) => set({ isLoaded: loaded }),
}));

/**
 * Check if system prefers dark mode
 * Falls back to false if matchMedia is not supported
 */
// const systemPrefersDark = (): boolean => {
//   try {
//     return (
//       window.matchMedia &&
//       window.matchMedia("(prefers-color-scheme: dark)").matches
//     );
//   } catch {
//     console.warn("matchMedia not supported, defaulting to light mode");
//     return false;
//   }
// };

/**
 * Apply dark mode class to document
 */
const applyDarkMode = (isDark: boolean) => {
  if (isDark) {
    document.documentElement.classList.add("ion-palette-dark");
  } else {
    document.documentElement.classList.remove("ion-palette-dark");
  }
};

/**
 * Hook for dark mode management
 * Loads preference from storage on mount and applies it
 * Falls back to system preference if no stored value
 */
export const useDarkMode = () => {
  const { isDarkMode, isLoaded, setDarkMode, setLoaded } = useDarkModeStore();

  // Load dark mode preference on mount
  useEffect(() => {
    const loadDarkMode = async () => {
      try {
        // const storedValue = await preferences.get(StorageKeys.DARK_MODE);

        // Dark mode disabled for now - always use light mode
        const isDark = false;

        setDarkMode(isDark);
        applyDarkMode(isDark);
      } catch (error) {
        console.error("Error loading dark mode preference:", error);
        report(error);
        // Dark mode disabled for now
        const isDark = false;
        setDarkMode(isDark);
        applyDarkMode(isDark);
      } finally {
        setLoaded(true);
      }
    };

    if (!isLoaded) {
      loadDarkMode();
    }
  }, [isLoaded, setDarkMode, setLoaded]);

  /**
   * Toggle dark mode and persist to storage
   */
  const toggleDarkMode = async (isDark: boolean) => {
    try {
      setDarkMode(isDark);
      applyDarkMode(isDark);
      await preferences.set(StorageKeys.DARK_MODE, isDark.toString());
    } catch (error) {
      console.error("Error saving dark mode preference:", error);
      report(error);
    }
  };

  return {
    isDarkMode,
    isLoaded,
    toggleDarkMode,
  };
};
